import { useEffect, useState, useCallback } from "react";
import api, { apiError, money } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/context/MetaContext";
import { PageHeader, Loading, EmptyState } from "@/components/common/PageHeader";
import { ResourceForm } from "@/components/common/ResourceForm";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, MoreVertical, Pencil, Archive, Store, MapPin } from "lucide-react";

const OUTLET_FIELDS = [
  { name: "name", label: "Outlet Name", span: 1, required: true },
  { name: "code", label: "Code", span: 1 },
  { name: "type", label: "Type", type: "select", span: 1, options: [
    { value: "restaurant", label: "Restaurant" }, { value: "hotel", label: "Hotel" },
    { value: "cafe", label: "Café" }, { value: "bar", label: "Bar" }, { value: "cloud_kitchen", label: "Cloud Kitchen" }] },
  { name: "status", label: "Status", type: "select", span: 1, options: [
    { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }, { value: "archived", label: "Archived" }], default: "active" },
  { name: "city", label: "City", span: 1 },
  { name: "phone", label: "Phone", span: 1 },
  { name: "address", label: "Address", type: "textarea" },
  { name: "monthly_budget", label: "Monthly Budget", type: "number", span: 1, step: "0.01", default: 0 },
  { name: "image", label: "Cover Image URL", span: 1 },
];

export default function Outlets() {
  const { can } = useAuth();
  const { reloadMeta } = useMeta();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/outlets"); setRows(data || []); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (values) => {
    setSaving(true);
    try {
      if (editing) await api.put(`/outlets/${editing.id}`, values);
      else await api.post("/outlets", values);
      toast.success(editing ? "Outlet updated" : "Outlet created");
      setOpen(false); setEditing(null); load(); reloadMeta();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const archive = async () => {
    try { await api.delete(`/outlets/${archiveTarget.id}`); toast.success("Outlet archived"); setArchiveTarget(null); load(); reloadMeta(); }
    catch (e) { toast.error(apiError(e)); setArchiveTarget(null); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Outlets" subtitle="Create, configure and compare outlets across the chain.">
        {can("outlets:create") && <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-outlet-btn"><Plus className="mr-1 h-4 w-4" /> New Outlet</Button>}
      </PageHeader>

      {loading ? <Loading /> : rows.length === 0 ? <EmptyState icon={Store} title="No outlets" hint="Create your first outlet." /> : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {rows.map((o) => (
            <div key={o.id} data-testid={`outlet-card-${o.id}`} className="group overflow-hidden rounded-sm border border-border bg-card transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg">
              <div className="relative h-36 overflow-hidden bg-secondary">
                {o.image && <img src={o.image} alt={o.name} className="h-full w-full object-cover" />}
                <div className="absolute right-2 top-2"><StatusBadge status={o.status} /></div>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-display text-lg font-bold tracking-tight">{o.name}</h3>
                    <p className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" />{o.city || "—"} · {o.code}</p>
                  </div>
                  {(can("outlets:edit") || can("outlets:delete")) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`outlet-menu-${o.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {can("outlets:edit") && <DropdownMenuItem onClick={() => { setEditing(o); setOpen(true); }}><Pencil className="mr-2 h-4 w-4" />Edit</DropdownMenuItem>}
                        {can("outlets:delete") && <DropdownMenuItem className="text-destructive" onClick={() => setArchiveTarget(o)}><Archive className="mr-2 h-4 w-4" />Archive</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 border-t border-border pt-3 text-sm">
                  <div><div className="eyebrow">Type</div><div className="mt-0.5 capitalize">{String(o.type).replace(/_/g, " ")}</div></div>
                  <div><div className="eyebrow">Monthly Budget</div><div className="mt-0.5 font-semibold tabular">{money(o.monthly_budget)}</div></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ResourceForm open={open} onOpenChange={setOpen} title={editing ? "Edit Outlet" : "New Outlet"} fields={OUTLET_FIELDS} initial={editing} onSubmit={save} submitting={saving} />
      <ConfirmDialog open={!!archiveTarget} onOpenChange={(v) => !v && setArchiveTarget(null)} title="Archive this outlet?" description="Historical records remain traceable. The outlet will be hidden from active operations." confirmText="Archive" destructive onConfirm={archive} />
    </div>
  );
}
